/**
 * CADBOT — assistant de la vitrine (téléphone).
 * - Suivi : référence REM… → /api/public/suivi (RGPD).
 * - Infos publiques (horaires, adresse, diagnostic, garantie…) : réponses immédiates locales.
 * - Prix / devis / autre : chat invité réel (/api/public/devis-chat) → réponse CADBOT côté serveur
 *   (prix validés, fournisseurs) puis devis par e-mail après validation par l'équipe.
 *
 * i18n : les textes AFFICHÉS passent par t('cadbot.*'). La DÉTECTION d'intention (localAnswer,
 * REF_IN_TEXT, /Trop de messages/, parsing « fr-FR » de l'heure de Cayenne) reste en mots FRANÇAIS
 * à dessein — le visiteur peut écrire en français et les chips envoient toujours la requête FR.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Bot, Send, Clock, Smartphone, BatteryCharging, Search, MapPin, RotateCcw, History } from 'lucide-react';
import { fetchSuivi, TrackResult } from './TrackRepair';
import { computeHoursState, HOURS_ROWS } from './HoursLive';
import { useLanguage } from '../../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL || '';
// Confidentialité : le jeton invité vit dans sessionStorage (cet onglet seulement). Il disparaît à la
// fermeture de l'onglet : un autre visiteur sur le même ordinateur ne voit jamais la conversation
// précédente. L'ancien widget stockait le jeton dans localStorage : on l'efface définitivement.
const STORAGE_KEY = 'rm_cadbot_guest_token';
const LEGACY_LOCAL_KEY = 'rm_homepage_devis_guest_token';
const REF_IN_TEXT = /\bREM\s?-?\s?(\d{2,8})\b/i;

// Le jeton expire après 30 min sans activité (sessionStorage survit à « rouvrir l'onglet fermé »).
const TOKEN_TTL_MS = 30 * 60 * 1000;
const dropToken = () => { try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ } };
const getToken = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return '';
    const { t, at } = JSON.parse(raw);
    if (!t || !at || Date.now() - at > TOKEN_TTL_MS) { dropToken(); return ''; }
    return t;
  } catch { dropToken(); return ''; }
};
const putToken = (t) => { try { if (t) sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ t, at: Date.now() })); } catch { /* ignore */ } };
const touchToken = (t) => putToken(t);
const dropLegacyToken = () => { try { localStorage.removeItem(LEGACY_LOCAL_KEY); } catch { /* ignore */ } };

// Les chips envoient toujours la requête FR (`q`) pour ne pas casser la détection d'intention ;
// seul le libellé affiché (`k`) est traduit.
const CHIPS = [
  { q: 'Où en est ma réparation ?', k: 'cadbot.chip.status', Icon: Clock },
  { q: 'Écran iPhone 11 cassé, combien ?', k: 'cadbot.chip.screen', Icon: Smartphone },
  { q: 'Batterie Galaxy S21, prix ?', k: 'cadbot.chip.battery', Icon: BatteryCharging },
  { q: 'Le diagnostic coûte combien ?', k: 'cadbot.chip.diagnostic', Icon: Search },
  { q: 'Horaires et adresse ?', k: 'cadbot.chip.hours', Icon: MapPin },
];

function hoursSentence(t) {
  // i18n : libellés jours/horaires tirés des clés hours.* (HoursLive) -> traduit dans les 7 langues.
  const rows = HOURS_ROWS.map((r) => (r.hours
    ? `${t(r.labelKey).toLowerCase()} ${t(r.hoursKey)}`
    : `${t(r.labelKey).toLowerCase()} ${t('cadbot.local.closedLower')}`)).join(', ');
  return rows;
}

/**
 * Réponse locale pour les questions d'information publique ; null → chat serveur.
 * Les regex d'ENTRÉE restent en français (le visiteur écrit en FR) ; seules les réponses
 * affichées sont traduites via `t`. `formatCurrency` localise les montants du diagnostic.
 */
export function localAnswer(raw, { address, phone, t, formatCurrency }) {
  const q = raw.toLowerCase();
  if (/^(bonjour|salut|hello|coucou|hey|bonsoir)\b/.test(q.trim())) {
    return t('cadbot.local.greeting');
  }
  if (/diagnostic/.test(q)) {
    return t('cadbot.local.diagnostic', {
      min: formatCurrency(35, 'EUR', { maximumFractionDigits: 0 }),
      max: formatCurrency(80, 'EUR', { maximumFractionDigits: 0 }),
    });
  }
  if (/\beau\b|oxyd|humid|tomb[ée]|mouill/.test(q)) {
    return t('cadbot.local.water');
  }
  if (/horaire|adresse|ouvert|ferm[ée]|boutique|magasin|o[uù] (vous )?trouver/.test(q)) {
    let now;
    try {
      const f = new Intl.DateTimeFormat('fr-FR', { timeZone: 'America/Cayenne', weekday: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
      const o = {}; f.formatToParts(new Date()).forEach((p) => { o[p.type] = p.value; });
      const wd = { dim: 0, lun: 1, mar: 2, mer: 3, jeu: 4, ven: 5, sam: 6 }[(o.weekday || '').toLowerCase().replace('.', '').slice(0, 3)];
      now = computeHoursState({ d: wd === undefined ? new Date().getDay() : wd, sec: ((+o.hour) % 24) * 3600 + (+o.minute) * 60 + (+o.second) }, t);
    } catch { now = null; }
    let state = '';
    if (now) {
      if (now.open) {
        const time = `${now.count.h ? `${now.count.h} h ` : ''}${now.count.m} min`;
        state = t('cadbot.local.hoursOpen', { state: now.state, time, countLabel: now.countLabel });
      } else {
        state = t('cadbot.local.hoursClosed', { sub: now.sub.split(' · ')[0] });
      }
    }
    return t('cadbot.local.hoursLine', { address, hours: hoursSentence(t), state }).trim();
  }
  if (/garanti/.test(q)) return t('cadbot.local.warranty');
  if (/compte|connexion|connecter|espace|login|mot de passe/.test(q)) {
    return t('cadbot.local.accounts');
  }
  if (/(r[ée]paration|statut|suivi|o[uù] en est|avanc)/.test(q) && !/[ée]cran|batterie|connecteur|cam[ée]ra|prix|combien|tarif|devis/.test(q)) {
    return t('cadbot.local.trackAsk');
  }
  // Uniquement une question de suivi de devis : jamais un message qui contient un e-mail (capture serveur)
  // ni une description de panne / demande de prix (doit atteindre le serveur).
  if (/devis/.test(q) && !/@/.test(q) && !/[ée]cran|batterie|connecteur|cam[ée]ra|vitre|coque|iphone|samsung|galaxy|xiaomi|huawei|prix|combien|tarif|pour (un|une|mon|ma)/.test(q)
    && /(o[uù] en est|toujours pas|pas re[çc]u|re[çc]u mon devis|statut de mon devis|suivi de mon devis)/.test(q)) {
    return t('cadbot.local.quoteFollowup', { phone });
  }
  if (/whatsapp|t[ée]l[ée]phone|appeler|num[ée]ro/.test(q)) return t('cadbot.local.contact', { phone });
  return null;
}

// Liens http(s) cliquables dans les messages du bot (ex. lien d'acompte SumUp) — jamais de HTML injecté.
const URL_RE = /(https?:\/\/[^\s<>"']+)/g;
function linkify(str, keyPrefix) {
  const out = [];
  let last = 0;
  let m;
  URL_RE.lastIndex = 0;
  while ((m = URL_RE.exec(str)) !== null) {
    let url = m[0];
    let trail = '';
    // ponctuation finale hors du lien ( « ... :  https://x.y/z. » )
    while (/[.,;:!?)\]]$/.test(url)) { trail = url.slice(-1) + trail; url = url.slice(0, -1); }
    if (m.index > last) out.push(str.slice(last, m.index));
    out.push(<a key={`${keyPrefix}-${m.index}`} href={url} target="_blank" rel="noopener noreferrer">{url}</a>);
    if (trail) out.push(trail);
    last = m.index + m[0].length;
  }
  if (last < str.length) out.push(str.slice(last));
  return out;
}

function rich(text) {
  const parts = String(text || '').split('**');
  return parts.map((p, i) => (i % 2 ? <b key={i}>{linkify(p, `b${i}`)}</b> : <React.Fragment key={i}>{linkify(p, `t${i}`)}</React.Fragment>));
}

export default function CadbotChat({ address, phone }) {
  const { t, formatCurrency } = useLanguage();
  // Message d'accueil recalculé à chaque rendu (langue courante) ; l'id 'w' reste stable.
  const welcome = { id: 'w', role: 'bot', text: t('cadbot.welcome') };
  const [msgs, setMsgs] = useState(() => [welcome]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  // Jamais de jeton au 1er paint : l'historique serveur ne s'affiche qu'après « Reprendre » ou un nouveau message.
  const [token, setToken] = useState('');
  const [canResume, setCanResume] = useState(false);
  const boxRef = useRef(null);
  const seq = useRef(0);
  // Le serveur demande nom / téléphone / e-mail avant le devis : on laisse passer toutes les réponses
  const awaitingRef = useRef(false);
  const [awaitingId, setAwaitingId] = useState(false);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [msgs, typing]);

  // Purge agressive au montage : ancien localStorage + jeton sessionStorage mal formé.
  // Accueil = WELCOME uniquement. Reprise uniquement via bouton explicite.
  useEffect(() => {
    dropLegacyToken();
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw && raw[0] !== '{') dropToken();
    } catch { /* ignore */ }
    setCanResume(!!getToken());
  }, []);

  const resumeConversation = async () => {
    const tok = getToken();
    if (!tok) { setCanResume(false); return; }
    setTyping(true);
    try {
      const r = await fetch(`${API}/api/public/devis-chat/messages`, { headers: { 'X-Guest-Token': tok } });
      const data = r.ok ? await r.json() : null;
      const list = (data?.messages || []).filter((m) => m && m.content).slice(-8);
      if (!list.length) { dropToken(); setToken(''); setCanResume(false); return; }
      setMsgs([welcome, ...list.map((m) => ({ id: m.id, role: m.sender_role === 'client' ? 'me' : 'bot', text: m.content }))]);
      setToken(tok); touchToken(tok);
    } catch { /* on reste sur l'accueil */ } finally { setTyping(false); setCanResume(false); }
  };

  const resetConversation = () => {
    dropToken(); setToken(''); setCanResume(false); setMsgs([welcome]); setInput('');
  };

  const push = (m) => { seq.current += 1; setMsgs((prev) => [...prev, { id: `${Date.now()}-${seq.current}`, ...m }]); };

  const ensureSession = async () => {
    if (token) {
      // Onglet resté ouvert : au-delà de 30 min sans activité, on repart sur une session neuve
      if (getToken() === token) return token;
      setToken(''); setMsgs([welcome]);
    }
    const r = await fetch(`${API}/api/public/devis-chat/session`, { method: 'POST' });
    if (r.status === 429) { const d = await r.json().catch(() => ({})); const e = new Error('RATE_LIMIT'); e.retryAfter = d.retry_after; throw e; }
    if (!r.ok) throw new Error('session');
    const data = await r.json();
    putToken(data.guest_token); setToken(data.guest_token);
    awaitingRef.current = false; setAwaitingId(false);
    return data.guest_token;
  };

  const askServer = async (text) => {
    let tok = await ensureSession();
    let r = await fetch(`${API}/api/public/devis-chat/messages`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ guest_token: tok, content: text }),
    });
    if (r.status === 404) { // session expirée → nouvelle session
      dropToken();
      setToken('');
      tok = await (async () => { const s = await fetch(`${API}/api/public/devis-chat/session`, { method: 'POST' }); const d = await s.json(); putToken(d.guest_token); setToken(d.guest_token); return d.guest_token; })();
      r = await fetch(`${API}/api/public/devis-chat/messages`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ guest_token: tok, content: text }),
      });
    }
    const data = await r.json().catch(() => ({}));
    if (r.status === 429) { const e = new Error('RATE_LIMIT'); e.retryAfter = data.retry_after; throw e; }
    if (!r.ok) throw new Error(data.detail || 'Erreur envoi');
    touchToken(tok);
    const aw = Array.isArray(data.awaiting_identity) ? data.awaiting_identity.length > 0 : !!data.awaiting_email;
    awaitingRef.current = aw; setAwaitingId(aw);
    return data.ai_message?.content || t('cadbot.server.ackDefault');
  };

  // `raw` = texte traité (détection FR) ; `display` = libellé affiché dans la bulle (chips traduits).
  const send = async (raw, display) => {
    const text = String(raw || '').trim();
    if (!text || typing) return;
    push({ role: 'me', text: display != null ? display : text });
    setTyping(true);
    const delay = (ms) => new Promise((res) => setTimeout(res, ms));
    try {
      const m = (awaitingRef.current || /@/.test(text)) ? null : text.match(REF_IN_TEXT); // une adresse e-mail n'est jamais une référence
      if (m) {
        const ref = `REM${m[1]}`;
        try {
          const [data] = await Promise.all([fetchSuivi(ref), delay(500)]);
          push({ role: 'bot', text: t('cadbot.track.here', { ref }), node: <TrackResult data={data} compact /> });
        } catch (err) {
          push({ role: 'bot', text: err?.response?.status === 404 ? t('cadbot.track.notFound', { ref }) : t('cadbot.track.unavailable') });
        }
        return;
      }
      const local = awaitingRef.current ? null : localAnswer(text, { address, phone, t, formatCurrency });
      if (local) { await delay(550); push({ role: 'bot', text: local }); return; }
      const reply = await askServer(text);
      push({ role: 'bot', text: reply });
    } catch (err) {
      const wait = err?.retryAfter ? t('cadbot.error.waitMinutes', { n: Math.max(1, Math.ceil(err.retryAfter / 60)) }) : t('cadbot.error.waitFallback');
      push({ role: 'bot', text: (err?.message === 'RATE_LIMIT' || /Trop de messages/.test(err?.message || '')) ? t('cadbot.error.rateLimit', { wait }) : t('cadbot.error.server', { phone }) });
    } finally {
      setTyping(false);
    }
  };

  return (
    <div className="chat-grid" data-testid="cadbot-section">
      <div className="chips">
        {CHIPS.map(({ q, k, Icon }) => (
          <button key={q} type="button" className="chip" onClick={() => send(q, t(k))}><Icon /> {t(k)}</button>
        ))}
        <p className="note">{t('cadbot.note')}</p>
      </div>
      <div className="phone">
        <div className="screen">
          <div className="cbar">
            <div className="cav"><Bot /></div>
            <div className="who"><b>CADBOT</b><span>● {t('cadbot.status.online')}</span></div>
            {canResume && !token && (
              <button type="button" className="reset" onClick={resumeConversation} title={t('cadbot.resume.title')} data-testid="cadbot-resume">
                <History /> {t('cadbot.resume.label')}
              </button>
            )}
            {token && (
              <button type="button" className="reset" onClick={resetConversation} title={t('cadbot.reset.title')} data-testid="cadbot-reset">
                <RotateCcw /> {t('cadbot.reset.label')}
              </button>
            )}
          </div>
          <div className="msgs" ref={boxRef} aria-live="polite">
            {msgs.map((m) => (
              <div key={m.id} className={`msg ${m.role}`}>{m.role === 'bot' ? rich(m.text) : m.text}{m.node || null}</div>
            ))}
            {typing && <div className="typing"><i /><i /><i /></div>}
          </div>
          <form className="cin" autoComplete="off" onSubmit={(e) => { e.preventDefault(); const v = input; setInput(''); send(v); }}>
            <label className="sr-only" htmlFor="rmv-q">{t('cadbot.input.label')}</label>
            <input id="rmv-q" className="input" value={input} onChange={(e) => setInput(e.target.value)} placeholder={awaitingId ? t('cadbot.input.placeholderIdentity') : t('cadbot.input.placeholderDefault')} data-testid="cadbot-input" />
            <button type="submit" aria-label={t('cadbot.send.aria')} disabled={typing || !input.trim()} data-testid="cadbot-send"><Send /></button>
          </form>
        </div>
      </div>
    </div>
  );
}
